import useSWR, { mutate } from 'swr'
import type {
  Component,
  ComponentDetails,
  ComponentQueryParams,
  ComponentSearchParams,
  ComponentSearchResponse,
  ExpandedTarget,
  ExpandedTargetSearchResponse,
  LabelResponse,
  MetricDetailsParams,
  MetricDetailsResponse,
  MetricQueryParams,
  MetricSearchResponse,
  NeededTargetMetric,
  SearchNeededTargetMetricsParams,
  TargetDetailsParams,
  TargetDetailsResponse,
  TargetQueryParams,
  TargetSearchResponse,
} from 'types/components'
import { generateDefaultQueryParams } from 'types/default-params'
import {
  fetchWithToken,
  fetchWithTokenWithStatus,
  postWithToken,
  postWithTokenWithStatus,
  deleteWithToken,
} from './http'
import { useState, useCallback } from 'react'

export function useFetchOrganizationComponents(token: string, orgId: string, params: [[string, string]] | null) {
  const { data, error, isLoading } = useSWR(
    [`components/organization/${orgId}`, token, params],
    ([url, token, params]) => fetchWithToken(url, token, params)
  )

  return {
    components: data ? (data as Component[]) : [],
    isLoading,
    isError: error,
  }
}

interface UseGetComponentByIdReturn {
  details: ComponentDetails | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<ComponentDetails | null>
}

export function useFetchRootOrganizationComponents(token: string, orgId: string, params: [[string, string]] | null) {
  const { data, error, isLoading } = useSWR(
    orgId ? [`components/root-organization/${orgId}`, token, params] : null,
    ([url, token, params]) => fetchWithToken(url, token, params)
  )

  return {
    components: data ? (data as Component[]) : [],
    isLoading,
    isError: error,
  }
}

interface UseGetComponentByIdReturn {
  details: ComponentDetails | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<ComponentDetails | null>
}

export function useGetComponentById(
  token: string,
  componentID: string,
  shouldFetch: boolean
): UseGetComponentByIdReturn {
  const { data, error, isValidating, mutate } = useSWR(
    shouldFetch ? [`components/${componentID}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      suspense: true,
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<ComponentDetails | null> => {
    const refetchedData = await mutate()
    return refetchedData as ComponentDetails | null
  }

  return {
    details: data as ComponentDetails | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

export function useFetchExpandedTargets(token: string, rootOrganizationId: string, rootPeriodId: string) {
  const shouldFetch = token != null && token.trim() !== ''

  const { data, error, isValidating } = useSWR(
    shouldFetch && rootOrganizationId && rootPeriodId
      ? [`components/period/target/expanded?organizationId=${rootOrganizationId}&rootPeriodId=${rootPeriodId}`, token]
      : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      suspense: false,
      revalidateIfStale: true,
      revalidateOnFocus: true,
    }
  )

  return {
    targets: data as ExpandedTarget[],
    isLoadingExpandedTargets: !data && isValidating,
    isError: error,
  }
}

export function useSearchTargets(token: string, queryParams: TargetQueryParams, shouldFetch: boolean = true) {
  const params = generateDefaultQueryParams(queryParams)
  params.push(['organizationIds', queryParams.organizationIds.join(',')])
  params.push(['periodIds', queryParams.periodIds.join(',')])
  if (queryParams.isOrganizationLevel !== undefined) {
    params.push(['isOrganizationLevel', queryParams.isOrganizationLevel.toString()])
  }
  if (queryParams.isArchived !== undefined) {
    params.push(['isArchived', queryParams.isArchived.toString()])
  }
  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`components/targets/search`, token, params] : null, // Conditional key based on shouldFetch
    ([url, token, queryParams]) => fetchWithToken(url, token, queryParams)
  )

  const handleRefetch = () => {
    mutate(['components/targets/search', token, params], fetchWithToken('components/targets/search', token!, params))
  }

  return {
    targets: data as TargetSearchResponse,
    isLoading,
    isError: error,
    refetch: handleRefetch,
  }
}

export function useSearchExpandedTargets(token: string, params: ComponentSearchParams, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`components/targets/expanded/search`, token, params] : null,
    ([url, token, params]) => postWithToken(url, token, params)
  )

  return {
    targets: data as ExpandedTargetSearchResponse,
    isLoading,
    isError: error,
  }
}

export function useSearchMetrics(token: string, queryParams: MetricQueryParams, shouldFetch: boolean = true) {
  const params = generateDefaultQueryParams(queryParams)
  params.push(['periodIds', queryParams.periodIds.join(',')])
  params.push(['organizationIds', queryParams.organizationIds.join(',')])
  if (queryParams.isVisible !== undefined) {
    params.push(['isVisible', queryParams.isVisible.toString()])
  }
  if (queryParams.isOrganizationLevel !== undefined) {
    params.push(['isOrganizationLevel', queryParams.isOrganizationLevel.toString()])
  }
  if (queryParams.isArchived !== undefined) {
    params.push(['isArchived', queryParams.isArchived.toString()])
  }
  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`components/period/search`, token, params] : null, // Conditional key based on shouldFetch
    ([url, token, params]) => fetchWithToken(url, token, params)
  )

  const revalidate = () => {
    if (token && params) {
      mutate([`components/period/search`, token, params])
    }
  }

  return {
    metrics: data as MetricSearchResponse,
    isLoading,
    isError: error,
    revalidate,
  }
}

export function useSearchNeededTargetMetrics(
  token: string,
  params: SearchNeededTargetMetricsParams,
  shouldFetch: boolean
) {
  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`components/targets/metrics/search`, token, params] : null,
    ([url, token, params]) => postWithToken(url, token, params)
  )

  return {
    targets: data as NeededTargetMetric[],
    isLoading,
    isError: error,
  }
}

export function useFetchTargetDetails(token: string, params: TargetDetailsParams, shouldFetch: boolean) {
  const key = shouldFetch ? [`components/targets/details`, token, JSON.stringify(params)] : null
  const { data, error, isLoading } = useSWR(
    key,
    async ([url, token, paramsString]) => {
      const parsedParams = JSON.parse(paramsString)
      return postWithToken(url, token, parsedParams)
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 0,
    }
  )

  const revalidate = () => {
    if (key) {
      mutate(key)
    }
  }

  return {
    targetDetails: data as TargetDetailsResponse,
    isLoading,
    isError: error,
    revalidate,
  }
}

export function useFetchMetricDetails(token: string, params: MetricDetailsParams, shouldFetch: boolean) {
  const key = shouldFetch ? [`components/metrics/details`, token, JSON.stringify(params)] : null
  const { data, error, isLoading } = useSWR(
    key,
    async ([url, token, paramsString]) => {
      const parsedParams = JSON.parse(paramsString)
      return postWithToken(url, token, parsedParams)
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 0,
    }
  )

  const revalidate = () => {
    if (key) {
      mutate(key)
    }
  }

  return {
    metricDetails: data as MetricDetailsResponse,
    isLoading,
    isError: error,
    revalidate,
  }
}

export function useSearchComponents(token: string, queryParams: ComponentQueryParams, shouldFetch: boolean = true) {
  const params = generateDefaultQueryParams(queryParams)
  params.push(['periodIds', queryParams.periodIds.join(',')])
  params.push(['organizationIds', queryParams.organizationIds.join(',')])
  params.push(['userIds', queryParams.userIds.join(',')])
  if (queryParams.isArchived !== undefined) {
    params.push(['isArchived', queryParams.isArchived.toString()])
  }
  const { data, error, isLoading } = useSWR(
    shouldFetch ? [`components/search`, token, params] : null,
    async ([url, token, params]) => {
      return fetchWithToken(url, token, params)
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 0,
    }
  )

  const revalidate = () => {
    if (token && params) {
      mutate([`components/search`, token, params])
    }
  }

  return {
    componentSearchResult: data as ComponentSearchResponse,
    isLoading,
    isError: error,
    revalidate,
  }
}

export function useGetComponentLabels(token: string, organizationId: string) {
  const { data, error, isLoading } = useSWR(
    token && organizationId ? [`components/${organizationId}/labels`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null)
  )

  return {
    labels: data ? (data as LabelResponse).labels : [],
    isLoading,
    isError: error,
  }
}

export function useGetRootOrganizationComponentLabelsWithStatus(
  token: string,
  organizationId: string,
  shouldFetch: boolean
) {
  const { data, error, isLoading } = useSWR(
    shouldFetch && token && organizationId ? [`components/organization/${organizationId}/labels`, token] : null,
    ([url, token]) => fetchWithTokenWithStatus(url, token, null)
  )

  return {
    labels: data?.data ? (data.data as LabelResponse).labels : [],
    status: data?.status,
    isLoading,
    isError: error,
  }
}

export function useUpdateMetricsVisibility(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateVisibility = useCallback(
    (data: {
      organizationId: string
      rootOrganizationId: string
      isVisible: boolean
      metrics: MetricDetailsParams[]
    }) => {
      setIsLoading(true)
      setError(null)

      return postWithTokenWithStatus('components/metrics/visibility', token, data)
        .then((result) => {
          setIsLoading(false)
          if (result.status !== 200) {
            const errorMessage = result.data?.error || `Server responded with status ${result.status}`
            setError(errorMessage)
            throw new Error(errorMessage)
          }
          return result
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'An error occurred')
          setIsLoading(false)
          throw err
        })
    },
    [token]
  )

  return {
    updateVisibility,
    isLoading,
    error,
  }
}

export function useDeleteTarget(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteTarget = useCallback(
    (params: TargetDetailsParams) => {
      setIsLoading(true)
      setError(null)

      // Construct the delete URL with query parameters
      const queryParams = new URLSearchParams({
        componentId: params.componentId,
        organizationId: params.organizationId,
        periodId: params.periodId,
        ...(params.userId && { userId: params.userId }),
        ...(params.revision !== undefined && { revision: params.revision.toString() }),
      })

      return deleteWithToken(`components/targets?${queryParams.toString()}`, token)
        .then((result) => {
          setIsLoading(false)

          // Invalidate relevant SWR cache keys based on the target's identifiers
          // Target has: componentId, organizationId, periodId, userId

          // 1. Invalidate component details to update approved targets count
          mutate([`components/${params.componentId}`, token])

          // 2. Invalidate target details cache for this specific target
          mutate([`components/targets/details`, token, JSON.stringify(params)])

          // 3. Invalidate targets search caches that might include this target
          // Only invalidate searches that could potentially include this organizationId and periodId
          mutate(
            (key) => {
              if (!Array.isArray(key) || key[0] !== 'components/targets/search') return false
              // Check if the search parameters include this target's organization or period
              const searchParams = key[2] // Third element contains the search parameters array
              if (Array.isArray(searchParams)) {
                const hasMatchingOrg = searchParams.some(
                  ([param, value]) => param === 'organizationIds' && value.split(',').includes(params.organizationId)
                )
                const hasMatchingPeriod = searchParams.some(
                  ([param, value]) => param === 'periodIds' && value.split(',').includes(params.periodId)
                )
                return hasMatchingOrg || hasMatchingPeriod
              }
              return false
            },
            undefined,
            { revalidate: true }
          )

          // 4. Invalidate expanded targets search cache for relevant searches
          mutate(
            (key) => {
              if (!Array.isArray(key) || key[0] !== 'components/targets/expanded/search') return false
              // Check if the search parameters could include this target
              const searchParams = key[2] // ComponentSearchParams object
              if (searchParams && typeof searchParams === 'object') {
                return (
                  searchParams.organizationIds?.includes(params.organizationId) ||
                  searchParams.periodId?.includes(params.periodId)
                )
              }
              return false
            },
            undefined,
            { revalidate: true }
          )

          // 5. Invalidate expanded targets by period cache if it matches this target's organization and period
          mutate(
            (key) => {
              if (!Array.isArray(key) || !key[0]?.includes('components/period/target/expanded')) return false
              // Check if the URL contains this target's organizationId and periodId
              const url = key[0]
              return (
                url.includes(`organizationId=${params.organizationId}`) && url.includes(`periodId=${params.periodId}`)
              )
            },
            undefined,
            { revalidate: true }
          )

          return result
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'An error occurred')
          setIsLoading(false)
          throw err
        })
    },
    [token]
  )

  return {
    deleteTarget,
    isLoading,
    error,
  }
}

export function useArchiveComponents(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const archiveComponents = useCallback(
    (data: { componentIds: string[]; rootOrganizationId: string }) => {
      setIsLoading(true)
      setError(null)

      return postWithTokenWithStatus('components/bulk-archive', token, data)
        .then((result) => {
          setIsLoading(false)
          if (result.status !== 200) {
            const errorMessage = result.data?.error || `Server responded with status ${result.status}`
            setError(errorMessage)
            throw new Error(errorMessage)
          }
          return result
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'An error occurred')
          setIsLoading(false)
          throw err
        })
    },
    [token]
  )

  return { archiveComponents, isLoading, error }
}

export function useUnarchiveComponents(token: string) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const unarchiveComponents = useCallback(
    (data: { componentIds: string[]; rootOrganizationId: string }) => {
      setIsLoading(true)
      setError(null)

      return postWithTokenWithStatus('components/bulk-unarchive', token, data)
        .then((result) => {
          setIsLoading(false)
          if (result.status !== 200) {
            const errorMessage = result.data?.error || `Server responded with status ${result.status}`
            setError(errorMessage)
            throw new Error(errorMessage)
          }
          return result
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'An error occurred')
          setIsLoading(false)
          throw err
        })
    },
    [token]
  )

  return { unarchiveComponents, isLoading, error }
}
